// Recurrence helpers shared by the item detail editor and the item list.
//
// The UI models the common cases as Google-Calendar-style choices:
//   - 'none'    — does not repeat (not a recurring item)
//   - 'every'   — every meeting (backend scheme "weekly": same weekday each meeting)
//   - 'monthly' — the Nth <weekday> of the month (backend scheme "meeting-ordinal")
//   - 'custom'  — any other backend scheme, configured through the Custom… modal
//
// Per-occurrence exceptions are stored as recurrence overrides: applied=false
// skips this meeting, applied=true force-includes an otherwise off-schedule one.
// These helpers mirror the backend Recurrence.Applies closely enough to
// dim/skip/include items without a server round-trip.

import type { Item, Recurrence } from '../types/agenda';

export type RecurrenceKind = 'none' | 'every' | 'monthly' | 'custom';

const WEEKDAY_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// Just the date portion (YYYY-MM-DD) of a date-only or RFC3339 string.
function dayOf(dateStr: string | undefined): string {
  return dateStr ? dateStr.slice(0, 10) : '';
}

// Convert a YYYY-MM-DD value to RFC3339 midnight UTC for the Go backend.
export function toRFC3339(dateStr: string): string {
  return `${dayOf(dateStr)}T00:00:00Z`;
}

function partsOf(dateStr: string): { y: number; m: number; d: number } {
  const [y, m, d] = dayOf(dateStr).split('-').map(Number);
  return { y, m, d };
}

// UTC epoch day number, for stable day arithmetic free of DST.
function epochDay(dateStr: string): number {
  const { y, m, d } = partsOf(dateStr);
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

// Weekday index (0=Sunday) of a date-only or RFC3339 string.
export function weekdayOf(dateStr: string | undefined, fallback = 0): number {
  const day = dayOf(dateStr);
  if (!day) return fallback;
  const { y, m, d } = partsOf(day);
  return new Date(y, m - 1, d).getDay();
}

// Ordinal (1-5) of the weekday within its month, e.g. the 4th Sunday → 4.
// The Nth occurrence of a weekday always falls in the Nth 7-day block, so this
// matches the backend's occurrence-counting ordinal for real weekday dates.
export function ordinalOf(dateStr: string | undefined): number {
  const day = Number(dayOf(dateStr).slice(8, 10));
  if (!day) return 1;
  return Math.floor((day - 1) / 7) + 1;
}

function isLastDayOfMonth(dateStr: string): boolean {
  const { y, m, d } = partsOf(dateStr);
  return new Date(y, m, 0).getDate() === d; // day 0 of next month = last of this
}

// Extract the BYSETPOS ordinal from a "BYSETPOS=N" pattern (default 1).
export function extractOrdinal(pattern: string | undefined): number {
  const m = pattern?.match(/BYSETPOS=(\d+)/);
  return m ? parseInt(m[1], 10) : 1;
}

function ordinalLabel(n: number): string {
  const suffix = ['th', 'st', 'nd', 'rd'][n] ?? 'th';
  return `${n}${suffix}`;
}

// Which of the UI choices the item currently represents.
export function recurrenceKind(item: Item): RecurrenceKind {
  if (!item.recurring) return 'none';
  const scheme = item.recurrence?.scheme;
  if (!scheme || scheme === 'weekly') return 'every';
  if (scheme === 'meeting-ordinal') return 'monthly';
  return 'custom';
}

// Label for the 'monthly' option, e.g. "4th Sunday of the month". The weekday
// comes from the meeting date; N is preserved from the item's current setting or
// defaults to the meeting date's own ordinal.
export function monthlyOptionLabel(item: Item, agendaDate: string): string {
  const weekday = weekdayOf(agendaDate);
  const ordinal =
    item.recurrence?.scheme === 'meeting-ordinal'
      ? extractOrdinal(item.recurrence.pattern)
      : ordinalOf(agendaDate);
  return `${ordinalLabel(ordinal)} ${WEEKDAY_FULL[weekday]} of the month`;
}

// Human-readable summary of a recurrence, shown as the selected value when an
// item is on a schedule that isn't one of the three named options.
export function describeRecurrence(rec: Recurrence | undefined): string {
  switch (rec?.scheme) {
    case 'weekly':
      return 'Every meeting';
    case 'biweekly':
      return 'Every other week';
    case 'month':
    case 'monthly':
      return 'Same date each month';
    case 'daily':
      return 'Every day';
    case 'yearly':
      return 'Same date each year';
    case 'week-of-month':
    case 'meeting-ordinal': {
      const ord =
        rec.scheme === 'meeting-ordinal' ? extractOrdinal(rec.pattern) : ordinalOf(rec.startDate);
      return `${ordinalLabel(ord)} ${WEEKDAY_FULL[weekdayOf(rec.startDate)]} of the month`;
    }
    case 'first-after-day':
      return `First ${WEEKDAY_FULL[weekdayOf(rec.startDate)]} on/after the start date`;
    case 'manual':
      return 'Manual (include per meeting)';
    default:
      return 'Custom schedule';
  }
}

// Whether the scheme (ignoring per-occurrence overrides) matches the date.
function schemeApplies(rec: Recurrence, agendaDate: string): boolean {
  const startWeekday = rec.startDate ? weekdayOf(rec.startDate) : weekdayOf(agendaDate);
  switch (rec.scheme) {
    case 'weekly':
      return weekdayOf(agendaDate) === startWeekday;
    case 'biweekly':
      if (weekdayOf(agendaDate) !== startWeekday) return false;
      return rec.startDate ? (epochDay(agendaDate) - epochDay(rec.startDate)) % 14 === 0 : true;
    case 'monthly':
      if (rec.startDate && isLastDayOfMonth(rec.startDate)) return isLastDayOfMonth(agendaDate);
      return partsOf(agendaDate).d === (rec.startDate ? partsOf(rec.startDate).d : partsOf(agendaDate).d);
    case 'meeting-ordinal':
      return weekdayOf(agendaDate) === startWeekday && ordinalOf(agendaDate) === extractOrdinal(rec.pattern);
    case 'week-of-month':
      return weekdayOf(agendaDate) === startWeekday && ordinalOf(agendaDate) === ordinalOf(rec.startDate);
    case 'daily':
      return true;
    case 'yearly': {
      const a = partsOf(agendaDate);
      const s = rec.startDate ? partsOf(rec.startDate) : a;
      return a.m === s.m && a.d === s.d;
    }
    case 'manual':
      return false; // opt in per meeting via an applied=true override
    default:
      return true;
  }
}

// Whether a recurring item applies on the given meeting date (overrides win).
export function itemAppliesOn(item: Item, agendaDate: string): boolean {
  if (!item.recurring) return false;
  const rec = item.recurrence;
  if (!rec) return true; // unconfigured recurring item applies to every meeting
  const day = dayOf(agendaDate);

  if (rec.startDate && day < dayOf(rec.startDate)) return false;
  if (rec.endDate && day > dayOf(rec.endDate)) return false;

  const override = rec.overrides?.find((o) => dayOf(o.date) === day);
  if (override) return override.applied;

  return schemeApplies(rec, agendaDate);
}

// Produce the item fields for a chosen simple recurrence kind. StartDate anchors
// the weekday (and start of the window) to the current meeting date.
export function setRecurrenceKind(item: Item, kind: RecurrenceKind, agendaDate: string): Item {
  let recurring = true;
  let recurrence: Recurrence | undefined;

  switch (kind) {
    case 'none':
      recurring = false;
      recurrence = undefined;
      break;
    case 'every':
      recurrence = { scheme: 'weekly', startDate: toRFC3339(agendaDate) };
      break;
    case 'monthly': {
      const ordinal =
        item.recurrence?.scheme === 'meeting-ordinal'
          ? extractOrdinal(item.recurrence.pattern)
          : ordinalOf(agendaDate);
      recurrence = {
        scheme: 'meeting-ordinal',
        pattern: `BYSETPOS=${ordinal}`,
        startDate: toRFC3339(agendaDate),
      };
      break;
    }
    case 'custom':
      recurrence = item.recurrence; // configured through the modal, not here
      break;
  }

  const next: Item = { ...item, recurring, recurrence };
  next.appliesThisMeeting = itemAppliesOn(next, agendaDate);
  return next;
}

// Apply a fully-configured recurrence (from the Custom… modal).
export function setRecurrence(item: Item, recurrence: Recurrence, agendaDate: string): Item {
  const next: Item = { ...item, recurring: true, recurrence };
  next.appliesThisMeeting = itemAppliesOn(next, agendaDate);
  return next;
}

// The override state for this meeting: false = skipped, true = force-included,
// undefined = follow the schedule.
export function occurrenceOverride(item: Item, agendaDate: string): boolean | undefined {
  const day = dayOf(agendaDate);
  return item.recurrence?.overrides?.find((o) => dayOf(o.date) === day)?.applied;
}

export function isManuallySkipped(item: Item, agendaDate: string): boolean {
  return occurrenceOverride(item, agendaDate) === false;
}

export function isForceIncluded(item: Item, agendaDate: string): boolean {
  return occurrenceOverride(item, agendaDate) === true;
}

// Base recurrence to attach overrides to. A recurring item may have a nil
// recurrence ("every meeting", legacy); anchor it to a weekly scheme with a
// concrete startDate so the backend can resolve the weekday (a zero startDate
// resolves to the wrong weekday).
function baseRecurrence(item: Item, agendaDate: string): Recurrence {
  return item.recurrence ?? { scheme: 'weekly', startDate: toRFC3339(agendaDate) };
}

function pinOverride(item: Item, agendaDate: string, applied: boolean): Item {
  const day = dayOf(agendaDate);
  const rec = baseRecurrence(item, agendaDate);
  const overrides = (rec.overrides ?? []).filter((o) => dayOf(o.date) !== day);
  overrides.push({ date: toRFC3339(agendaDate), applied });
  const next: Item = { ...item, recurring: true, recurrence: { ...rec, overrides } };
  next.appliesThisMeeting = applied;
  return next;
}

// Skip this occurrence (pin an applied=false override for the meeting date).
export function skipOccurrence(item: Item, agendaDate: string): Item {
  return pinOverride(item, agendaDate, false);
}

// Force-include an otherwise off-schedule occurrence (applied=true override).
export function includeOccurrence(item: Item, agendaDate: string): Item {
  return pinOverride(item, agendaDate, true);
}

// Drop any override for the meeting date, reverting to the schedule. Used both
// to restore a skipped occurrence and to remove a force-include.
export function clearOccurrenceOverride(item: Item, agendaDate: string): Item {
  const day = dayOf(agendaDate);
  const rec = baseRecurrence(item, agendaDate);
  const kept = (rec.overrides ?? []).filter((o) => dayOf(o.date) !== day);
  const next: Item = {
    ...item,
    recurring: true,
    recurrence: { ...rec, overrides: kept.length ? kept : undefined },
  };
  next.appliesThisMeeting = itemAppliesOn(next, agendaDate);
  return next;
}
